import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Flex } from "@chakra-ui/react";
import Header from "../../components/reusables/Header/Header";
import ServiceMenu from "../../components/ServiceMenu/ServiceMenu";
import Footer from "../../components/includes/Footer/Footer";
import { findMenuByPath } from "../../components/reusables/Navbar/navConfig";
import { useDispatch, useSelector } from "react-redux";
import { bharatConnect } from "assets/images";
import CustomButton from "layouts/CustomButton/CustomButton";
import { NewUniversalField } from "layouts/NewUniversalField/NewUniversalField";
import {
  isMandatory,
  isMandatoryOrOptional,
  useBillerList,
} from "hooks/Bbps/billerList.ts/useBillerList";
import { BillerMaster } from "features/bbps/biller/billerTypes";
import { mapCustomerParamsToFields } from "features/bbps/biller/billerFieldMapper";
import { useFormik } from "formik";
import { billFetchSuccess } from "store/features/billerDetails/billerDetailAction";
import { buildCustomerParamsValidator } from "features/bbps/biller/billerValidation";
import { useBillFetch } from "hooks/Bbps/billFetch/useBillFetch";
import { usePaymentFlowStore } from "hooks/Bbps/paymentFlow/usePaymentFlowStore";
import { RootState } from "store/rootReducer";
import { useBillerPlans } from "hooks/Bbps/billerPlan/useBillerPlans";
import BillerPlanList from "components/Bbps/BillerPlanList/BillerPlanList";
import {
  UniversalSelect,
  UniversalSelectItem,
} from "layouts/UniversalSelect/UniversalSelect";
import { useBillValidation } from "hooks/Bbps/billValidation/useBillValidation";
import { useBillPayment } from "hooks/Bbps/billPayment/useBillPayment";
import { fetchBillerRegionsRequested } from "store/bbps/billerRegions/billerRegionsActions";
import { rupeeToPaisaStr } from "utils/moneyConverter/money";

export interface ServicesPageProps {
  id?: string;
  name?: string;
}

const ServicesPage: React.FC<ServicesPageProps> = () => {
  const [_search, setSearch] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const flow = usePaymentFlowStore();
  const dispatch = useDispatch();
  const serviceItem = findMenuByPath(location.pathname);
  const categoryApiName = serviceItem?.apiName;
  const categoryTitle = serviceItem?.label ?? "Service";

  const { selectedPlan } = flow;

  const billerRegionsState = useSelector((s: RootState) => s.billerRegions);

  const {
    items: billerRegions,
    loading: regionsLoading,
    error: regionsError,
  } = billerRegionsState;

  const {
    billers,
    loading: billersLoading,
    error: billersError,
  } = useBillerList(categoryApiName);

  const {
    fetchPlans,
    plans: plans,
    loading: plansLoading,
    error: plansError,
    resetPlans,
  } = useBillerPlans();

  const {
    fetchBill,
    loading: billFetchLoading,
    error: billFetchError,
    reset: resetBillFetch,
  } = useBillFetch();

  const {
    validateBill,
    resetValidation,
    loading: validationLoading,
    error: validationError,
    data: validationData,
    isBillValidationSuccess,
  } = useBillValidation();

  const { resetPayment } = useBillPayment();
  const [selectedBillerId, setSelectedBillerId] = useState<string>("");
  const [selectedBillerName, setSelectedBillerName] = useState<string>("");

  const selectedBiller: BillerMaster | undefined = useMemo(
    () => billers.find((b) => b.billerId === selectedBillerId),
    [billers, selectedBillerId],
  );

  const { fields, initialValues, fieldIdToParamName } = useMemo(
    () =>
      mapCustomerParamsToFields(selectedBiller?.billerCustomerParams, {
        billerId: selectedBillerId,
        categoryName: selectedBiller?.billerCategoryName,
        // categoryName: categoryApiName,
      }),
    [selectedBiller, selectedBillerId],
  );

  const circleFieldId = useMemo(() => {
    for (const [fieldId, paramName] of Object.entries(fieldIdToParamName)) {
      const name = paramName.toLowerCase();
      if (
        name.includes("circle") ||
        name.includes("location/circle") ||
        name.includes("circle location") ||
        name.includes("location")
      ) {
        return fieldId;
      }
    }
    return null;
  }, [fieldIdToParamName]);

  const validate = useMemo(
    () => buildCustomerParamsValidator(fields),
    [fields],
  );

  const billerItems: UniversalSelectItem[] = useMemo(
    () =>
      billers.map((b) => ({
        label: b.billerName, // for UI display
        // value: b.billerId, // internal value
        value: b.billerName,
      })),
    [billers],
  );

  useEffect(() => {
    if (!selectedBiller || !circleFieldId) {
      return;
    }

    dispatch(
      fetchBillerRegionsRequested({
        search: "IND",
        // page: 1,
        // limit: 200,
      }),
    );
  }, [selectedBiller, circleFieldId, dispatch]);

  const handleBillerChange = (value: string | string[]) => {
    const billerName = Array.isArray(value) ? (value[0] ?? "") : (value ?? "");

    // if user choose "-- choose --" type then blank select
    if (!billerName) {
      setSelectedBillerId("");
      setSelectedBillerName("");
      resetBillFetch();
      flow.reset?.();
      resetPlans();
      resetValidation();
      resetBillFetch();
      resetPayment();
      // dispatch(billPaymentReset());
      return;
    }

    const biller = billers.find((b) => b.billerName === billerName);

    if (!biller) return;

    let billerId = biller.billerId;
    // const billerName = biller.billerName;
    if (billerId === "NPCIPREP0NAT02") {
      billerId = "NPCIPRESINAT02";
    }

    setSelectedBillerName(billerName);
    setSelectedBillerId(billerId);

    resetBillFetch();
    flow.reset?.();
    resetPlans();
    resetValidation();
    resetPayment();
    // dispatch(billPaymentReset());

    // biller added
    flow.setBiller(biller);
    flow.setStep("ENTER_DETAILS");

    // plan flow:
    if (isMandatoryOrOptional(biller.planMdmRequirement)) {
      flow.setStep("PLANS_LOADING");
      fetchPlans({ search: [biller.billerId] });
    }
  };

  // if biller change flow update (safety)
  useEffect(() => {
    if (!selectedBiller) return;

    flow.setBiller(selectedBiller);

    if (!isMandatoryOrOptional(selectedBiller.planMdmRequirement)) {
      flow.setStep("ENTER_DETAILS");
    }
  }, [selectedBiller]);

  useEffect(() => {
    setSelectedBillerId("");
    setSelectedBillerName("");
    resetBillFetch();
    flow.reset?.();
    resetPlans();
    resetValidation();
    resetPayment();
    // dispatch(resetBillerRegions());
    // dispatch(billPaymentReset());
  }, [categoryApiName]);

  // Formik for dynamic fields
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, helpers) => {
      // STRICT validation on submit (regex included if enabled)
      const submitValidate = buildCustomerParamsValidator(fields, "submit");
      const submitErrors = submitValidate(values);

      if (Object.keys(submitErrors).length > 0) {
        // show errors on submit
        helpers.setErrors(submitErrors);

        // mark all fields touched so UI shows messages
        const touched: Record<string, boolean> = {};
        for (const f of fields) touched[f.id] = true;
        helpers.setTouched(touched, false);

        return;
      }

      if (!selectedBiller) return;

      // 1) customerParams map
      const customerParams: Record<string, string> = {};
      Object.entries(values).forEach(([fieldId, v]) => {
        const originalName = fieldIdToParamName[fieldId];
        if (originalName) customerParams[originalName] = String(v ?? "");
      });

      const customerMobile = "7378926752";
      const customerDetails: Record<string, string> = {
        "REMITTER NAME": "Rahul test",
      };

      flow.setBillFetchContext({
        customerParams,
        customerDetails,
        customerMobile,
        // referralNumber: "xyz",
      });

      const { planMdmRequirement, fetchRequirement, supportBillValidation } =
        selectedBiller;

      if (isMandatory(fetchRequirement)) {
        flow.setStep("BILL_FETCHING");
        fetchBill({
          billerId: selectedBiller.billerId,
          customerMobileNumber: "7378926752",
          customerDetails: {
            "REMITTER NAME": "Rahul test",
          },
          customerParams,
          deviceDetails: {
            MAC: "04-D9-C8-64-5E-3F",
            IP: "122.160.88.102",
          },
          timeStamp: "2025-11-28T02:21:13+05:30",
        });
      } else if (isMandatoryOrOptional(supportBillValidation)) {
        flow.setStep("VALIDATING");

        validateBill({
          billerId: selectedBiller.billerId,
          customerParams,
        });
        //  validateBill hook
      } else if (isMandatory(planMdmRequirement)) {
        //  plans hook
      } else {
        // direct payment flow (rare)
      }
    },
  });

  const { setFieldValue } = formik;

  const billFetchSlice = useSelector((s: RootState) => s.billFetch);

  const isSubmitting = billFetchLoading || validationLoading;

  // NEW – kaun sa dynamic field plan ID hai
  const planIdFieldId = useMemo(() => {
    const match = Object.entries(fieldIdToParamName).find(
      ([_fieldId, paramName]) =>
        typeof paramName === "string" &&
        /(^id$|id$|_id$|planid$)/i.test(paramName.trim()),
    );
    return match ? match[0] : null;
  }, [fieldIdToParamName]);

  useEffect(() => {
    const res = billFetchSlice.data;
    if (!res || !selectedBiller) return;

    // Zustand flow state update
    flow.setBillFetchResult(res);

    dispatch(billFetchSuccess(res));

    navigate("/service-details", {
      state: { fromCategoryPath: location.pathname }, // current /:categoryName
    });
  }, [billFetchSlice.data]);

  useEffect(() => {
    if (!isBillValidationSuccess || !validationData || !selectedBiller) {
      return;
    }

    if (flow.refId) {
      return;
    }
    // amount comes from plan
    const planAmount =
      flow.selectedPlan?.amountinrupees ??
      (flow.selectedPlan as any)?.amount ?? // optional fallback
      "0";

    flow.setBillValidationResult(
      String(validationData.refId),
      String(rupeeToPaisaStr(planAmount)),
    );

    navigate("/service-details", {
      state: { fromCategoryPath: location.pathname }, // current /:categoryName
    });
  }, [
    isBillValidationSuccess,
    validationData,
    selectedBiller,
    flow,
    navigate,
    location.pathname,
  ]);

  const lastAppliedPlanIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedPlan) return;

    if (lastAppliedPlanIdRef.current === selectedPlan.id) {
      return;
    }

    const match = Object.entries(fieldIdToParamName).find(
      ([_fieldId, paramName]) =>
        typeof paramName === "string" &&
        /(^id$|id$|_id$|planid$)/i.test(paramName.trim()),
    );

    if (!match) return;

    const [fieldId] = match;

    // id field me plan ka id set karo
    setFieldValue(fieldId, selectedPlan.id ?? "");

    // mark kar do ki is planId ke liye already value set ho chuki hai
    lastAppliedPlanIdRef.current = selectedPlan.id ?? null;
  }, [selectedPlan, fieldIdToParamName, setFieldValue]);

  const getFieldError = (fieldId: string) =>
    (formik.touched as any)[fieldId] && (formik.errors as any)[fieldId]
      ? ((formik.errors as any)[fieldId] as string)
      : undefined;

  return (
    <Box bg={"brand.5"} position={"relative"}>
      <Header />
      <ServiceMenu />

      <Box
        minHeight={"70vh"}
        flexDirection={{ base: "column", md: "row" }}
        display={"flex"}
        // flexWrap="wrap"
        bg="green.100"
      >
        <Box
          // w={["100%", "50%", "35%"]}
          w={{ base: "100%", md: "35%" }}
        >
          <Box>
            <Box
              m={["1rem", "2rem", "3rem"]}
              rounded={["md", "lg", "xl"]}
              px={["15px", "15px", "35px"]}
              py={["5px", "10px", "15px"]}
              bg="white"
              boxShadow={"xl"}
            >
              <Flex justify="space-between">
                <Box
                  fontSize={["sm", "md", "md"]}
                  fontWeight="bold"
                  p="0 0 0.5rem"
                >
                  {categoryTitle}
                </Box>
                <Box w={16}>
                  <img src={bharatConnect} alt="logo" />
                </Box>
              </Flex>

              {/* Error banner for any high-level error */}
              {billersError && (
                <Alert.Root status="error" mt={2} mb={2}>
                  <Alert.Indicator>{/* <Alert.Icon /> */}</Alert.Indicator>
                  <Alert.Content>
                    <Alert.Title fontSize="sm">
                      {String(billersError)}
                    </Alert.Title>
                  </Alert.Content>
                </Alert.Root>
              )}

              {/* Biller dropdown */}
              <Box mt={3} mb={3}>
                <UniversalSelect
                  id="billerId"
                  label="Select Biller"
                  placeholder={"-- choose --"}
                  items={billerItems}
                  // value={selectedBillerId}
                  value={selectedBillerName}
                  // defaultValue={selectedBillerId}
                  multiple={false}
                  loading={billersLoading}
                  // disabled={billersLoading || billers.length === 0}
                  disabled={
                    billersLoading || billers.length === 0 || isSubmitting
                  }
                  onChange={handleBillerChange}
                  //set UniversalSelect remote-mode
                  onSearch={(q) => setSearch(q)}
                  minSearchChars={3}
                  debounceMs={400}
                />
              </Box>

              {/* Dynamic form */}
              {selectedBiller && fields.length > 0 && (
                <form onSubmit={formik.handleSubmit}>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {fields.map((field) => {
                      const fieldId = field.id;
                      const isCircleField = circleFieldId === fieldId;

                      if (isCircleField) {
                        const regionItems = billerRegions.map((r: any) => ({
                          label: r.regionName,
                          value: r.regionName,
                          // if want regionCode
                          // value: r.regionCode,
                        }));

                        return (
                          <Box key={fieldId}>
                            <UniversalSelect
                              id={fieldId}
                              label={field.label ?? "Circle"}
                              value={formik.values[fieldId]}
                              items={regionItems}
                              multiple={false}
                              loading={regionsLoading}
                              disabled={
                                billersLoading ||
                                billers.length === 0 ||
                                isSubmitting
                              }
                              placeholder={
                                field.placeholder ?? "Select Circle / Location"
                              }
                              onChange={(val) =>
                                formik.setFieldValue(fieldId, val)
                              }
                              error={getFieldError(fieldId)}
                            />
                            {regionsError && (
                              <Box mt={1} fontSize="xs" color="red.500">
                                {regionsError}
                              </Box>
                            )}
                          </Box>
                        );
                      }
                      // Default case – existing generic field renderer
                      const isPlanIdField =
                        planIdFieldId === field.id &&
                        isMandatoryOrOptional(
                          selectedBiller?.planMdmRequirement,
                        );

                      const fieldForRender = isPlanIdField
                        ? {
                            ...field,
                            // plan select hone se pehle "Select Plan" dikhega,
                            // value aa jaane ke baad normal value dikhegi
                            placeholder:
                              selectedPlan && selectedPlan.id
                                ? (field.placeholder ?? field.label ?? "")
                                : "Select Plan",
                          }
                        : field;
                      return (
                        <NewUniversalField
                          key={field.id}
                          field={fieldForRender}
                          // field={field}
                          value={formik.values[field.id]}
                          onChange={(val) =>
                            formik.setFieldValue(field.id, val)
                          }
                          error={getFieldError(field.id)}
                          disabled={isSubmitting}
                          readOnly={isPlanIdField}
                        />
                      );
                    })}

                    {billFetchError && (
                      <Alert.Root
                        bg={"transparent"}
                        status="error"
                        mt={2}
                        mb={2}
                      >
                        <Alert.Indicator></Alert.Indicator>
                        <Alert.Content>
                          <Alert.Title fontSize="sm">
                            {billFetchError.message}
                          </Alert.Title>
                        </Alert.Content>
                      </Alert.Root>
                    )}
                    {validationError && (
                      <Alert.Root
                        bg={"transparent"}
                        status="error"
                        mt={2}
                        mb={2}
                      >
                        <Alert.Indicator></Alert.Indicator>
                        <Alert.Content>
                          <Alert.Title fontSize="sm">
                            {validationError.message}
                          </Alert.Title>
                        </Alert.Content>
                      </Alert.Root>
                    )}
                    <CustomButton
                      type="submit"
                      onClick={() => {}}
                      loading={billFetchLoading || validationLoading}
                      disabled={isSubmitting}
                    >
                      Submit
                    </CustomButton>
                  </Box>
                </form>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          flex="1"
          minW={0}
          px={{ base: 4, md: 6 }}
          py={{ base: 4, md: 6 }}
          className="bg-green-50"
          overflow="hidden"
        >
          {selectedBiller &&
            isMandatoryOrOptional(selectedBiller?.planMdmRequirement) && (
              <Box mt={2} mb={3} w="100%" maxW="100%">
                <Box fontSize="sm" fontWeight="semibold" mb={1}>
                  Choose a Plan
                </Box>
                <BillerPlanList
                  plans={plans}
                  loading={plansLoading}
                  errorMessage={plansError?.message}
                  disabled={isSubmitting}
                />
              </Box>
            )}
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default ServicesPage;
